import { useEffect, useRef, useState } from "react";
import { useModuleBEM } from "@b2bportal/core-themes";
import type { PlatformComponentProps } from "@b2bportal/core-types";
import type {
  VendorMessageSection,
  VendorMessageSubSection,
} from "@b2bportal/ground-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { htmlDecode } from "@hopper-b2b/utilities";
import { CarShopSection } from "@tenant/cars";
import {
  Button,
  Dialog,
  DialogTrigger,
  DialogContent,
  ButtonTypeEnum,
  Typography,
  DialogClose,
} from "@tenant/ui";
import clsx from "clsx";
import DOMPurify from "dompurify";
import defaultStyles from "./VendorMessage.module.scss";
import { useCarsStyles } from "~core-cars/themes";
import { CoreCarsComponent } from "~core-cars/types";

export type VendorMessageType = {
  messages?: VendorMessageSection;
} & PlatformComponentProps;

const COMPONENT_KEY = CoreCarsComponent.VendorMessages;

export const VendorMessages = ({ messages, className }: VendorMessageType) => {
  const styles = useCarsStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  return messages ? (
    <CarShopSection className={clsx(block, className)} title={messages.desc}>
      <div className={cn("content")}>
        {messages.details.map((message, i) => {
          return <VendorMessage key={i} message={message} />;
        })}
      </div>
    </CarShopSection>
  ) : null;
};

export const VendorMessage = ({
  message,
}: {
  message: VendorMessageSubSection;
}) => {
  const { t } = useI18nContext();
  const styles = useCarsStyles(COMPONENT_KEY, defaultStyles);
  const [_, cn] = useModuleBEM(styles, COMPONENT_KEY);
  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);
  useEffect(() => {
    if (textRef.current) {
      setIsTruncated(
        textRef.current.scrollHeight > textRef.current.clientHeight
      );
    }
  }, [message.text]);

  return message.text != null && message.text !== "" ? (
    <div className={cn("message")}>
      {message.title != null ? (
        <Typography type="heading" size="xxs" className={cn("message-title")}>
          {htmlDecode(message.title)}
        </Typography>
      ) : null}
      <div className={cn("message-content")}>
        <Typography
          ref={textRef}
          type="paragraph"
          variant="secondary"
          className={cn("message-text", ["short"])}
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(message.text),
          }}
        />
        {isTruncated && (
          <Dialog>
            <DialogTrigger asChild>
              <Button
                buttonType={ButtonTypeEnum.Link}
                className={cn("trigger")}
              >
                {t("readMore")}
              </Button>
            </DialogTrigger>
            <DialogContent
              title={
                message.title != null ? htmlDecode(message.title) : undefined
              }
              className={cn("modal")}
              bottomFixedContent={
                <div className={cn("bottom-section")}>
                  <DialogClose asChild className={cn("close-button")}>
                    <button className={cn("apply-button")}>
                      {t("close.button")}
                    </button>
                  </DialogClose>
                </div>
              }
            >
              <Typography
                className={cn("message-text")}
                type="paragraph"
                variant="secondary"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(message.text),
                }}
              />
            </DialogContent>
          </Dialog>
        )}
      </div>
    </div>
  ) : null;
};
